import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableBody from '@mui/material/TableBody';
import Checkbox from '@mui/material/Checkbox';
import MuiButton from '@mui/material/Button';
import { toast } from '../../../../ui/toast';
import { AddIcon, RefreshIcon } from '../../../../ui/icons/icons';
import { Modal } from '../../../../ui/modal/modal';
import { PaginationController } from '../../../../ui/pagination_controller/pagination_controller';
import {
  addToUserRoleList,
  deleteFromUserRoleList,
  editUserRoleList,
  getPermissionEntities,
} from '../../../../utils/api_service/user_roles';
import { Permission } from '../../../../utils/helpers/permissions';
import PermissionGate from '../../../../utils/hooks/PermissionGate';
import {
  addUserRoleRedux,
  deleteUserRoleRedux,
  editUserRoleRedux,
  fetchUserRoles,
} from '../../../../utils/redux_store/features/user_role_reducer';
import { RootState, store } from '../../../../utils/redux_store/store';
import { UserRole } from '../../../../utils/types/admin_types';
import TableSkeletonLoader from '../../../../ui/TableSkeletonLoader';
import baseStyles from '../../admin.module.css';
import { AdminSearchBar } from '../admin_search_bar/admin_search_bar';
import AddRoleModal from './modals/add_role_modal';
import EditRoleModal from './modals/edit_role_modal';
import { RoleRow } from './role_row/role_row';
import { useRoleSearch } from './useRoleSearch';

export const RoleManagement = () => {
  const userRolesState = useSelector((state: RootState) => state.userRoles);
  const userRoles = userRolesState?.userRoleList || [];
  const isLoading = userRolesState?.loading === 'pending';
  const [addRoleModalVisible, setAddRoleModalVisible] =
    useState<boolean>(false);
  const [editRoleModalVisible, setEditRoleModalVisible] =
    useState<boolean>(false);
  const [editedRole, setEditedRole] = useState<UserRole | undefined>(userRoles[0]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [permissionEntities, setPermissionEntities] = useState<string[]>([]);
  const { paginatedRoles, totalPages } = useRoleSearch(
    userRoles,
    searchTerm,
    pageNumber,
    8,
  );

  useEffect(() => {
    const fetchPermissionEntities = async () => {
      try {
        const response = await getPermissionEntities();
        if (response.ok) {
          const data = await response.json();
          setPermissionEntities(data);
        } else {
          const errorData = await response.json();
          toast.error(errorData.error, { position: 'bottom-center' });
        }
      } catch (error) {
        toast.error('Error fetching entites: ' + error, {
          position: 'bottom-center',
        });
      }
    };

    fetchPermissionEntities();
  }, []);

  const handleSearchTermChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setSearchTerm(event.target.value);
    setPageNumber(1);
  };

  const addNewRole = async (roleName: string, permissions: string[]) => {
    try {
      const response = await addToUserRoleList(roleName, permissions);
      if (response.ok) {
        const data = await response.json();
        setAddRoleModalVisible(false);
        store.dispatch(addUserRoleRedux(data));
      } else {
        const errorData = await response.json();
        toast.error(errorData.error, { position: 'bottom-center' });
      }
    } catch (error) {
      toast.error('Error adding role: ' + error, { position: 'bottom-center' });
    }
  };

  const deleteRole = async (id: string) => {
    try {
      const response = await deleteFromUserRoleList(id);
      if (response.ok) {
        store.dispatch(deleteUserRoleRedux(id));
      } else {
        const errorData = await response.json();
        toast.error(errorData.error, { position: 'bottom-center' });
      }
    } catch (error) {
      toast.error('Error deleting role: ' + error, {
        position: 'bottom-center',
      });
    }
  };

  const openEditModal = (role: UserRole) => {
    setEditedRole(role);
    setEditRoleModalVisible(true);
  };

  const editRole = async (
    role: UserRole,
    roleName: string,
    permissions: string[],
  ) => {
    try {
      const response = await editUserRoleList(role, roleName, permissions);
      if (response.ok) {
        const data = await response.json();
        setEditRoleModalVisible(false);
        store.dispatch(editUserRoleRedux(data));
      } else {
        const errorData = await response.json();
        toast.error(errorData.error, { position: 'bottom-center' });
      }
    } catch (error) {
      toast.error('Error editing role: ' + error, {
        position: 'bottom-center',
      });
    }
  };

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, mt: 2.5, mb: 2 }}>
        <AdminSearchBar
          searchTerm={searchTerm}
          handleChange={handleSearchTermChange}
        />
        <MuiButton
          variant='text'
          startIcon={<RefreshIcon />}
          onClick={() => store.dispatch(fetchUserRoles())}
          sx={{
            color: 'primary.main',
            fontWeight: 600,
            textTransform: 'capitalize',
            fontSize: '0.875rem',
            gap: 0.5,
          }}
        >
          Refresh
        </MuiButton>
        <Box sx={{ flex: 1 }} />
        <PermissionGate requiredPermissions={[Permission.ROLES_CREATE]}>
          <MuiButton
            variant='contained'
            startIcon={<AddIcon />}
            onClick={() => setAddRoleModalVisible(true)}
            sx={{
              borderRadius: '6px',
              textTransform: 'none',
              fontWeight: 600,
              px: 2,
              bgcolor: '#093725',
              '&:hover': { bgcolor: '#062619' },
            }}
          >
            Add Role
          </MuiButton>
        </PermissionGate>
      </Box>

      <Paper
        variant='outlined'
        sx={{
          borderRadius: '6px',
          borderColor: 'divider',
          overflow: 'hidden',
          boxShadow: 'none',
        }}
      >
        <TableContainer>
          <Table sx={{ minWidth: 500 }}>
            <TableHead>
              <TableRow sx={{ bgcolor: 'rgba(248, 250, 252, 0.95)' }}>
                <TableCell padding='checkbox' sx={{ pl: 2, width: 48 }}>
                  <Checkbox size='small' />
                </TableCell>
                <TableCell
                  sx={{
                    py: 1.5,
                    fontSize: '0.6875rem',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    color: 'text.secondary',
                  }}
                >
                  Role Name
                </TableCell>
                <TableCell align='right' sx={{ py: 1.5, pr: 2, width: 100 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={3} sx={{ py: 6, px: 2 }}>
                    <TableSkeletonLoader numRows={4} height={10} />
                  </TableCell>
                </TableRow>
              ) : paginatedRoles.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={3}
                    sx={{ py: 8, textAlign: 'center', color: 'text.secondary', fontSize: '0.875rem' }}
                  >
                    No user roles found.
                  </TableCell>
                </TableRow>
              ) : (
                paginatedRoles.map((role: UserRole) => (
                  <RoleRow
                    key={role.id}
                    role={role}
                    deleteRole={deleteRole}
                    onEditClick={openEditModal}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <PaginationController
        pageNumber={pageNumber}
        maxPageNumber={totalPages}
        navigateNextPage={() => setPageNumber((pageNumber) => pageNumber + 1)}
        navigatePreviousPage={() =>
          setPageNumber((pageNumber) => pageNumber - 1)
        }
      />
      <Modal
        visible={addRoleModalVisible}
        modalContent={
          <AddRoleModal entities={permissionEntities} addRole={addNewRole} />
        }
        toggle={() => setAddRoleModalVisible(false)}
        headerText='Add Role'
        headerClass={baseStyles.modalHeader}
      />
      <Modal
        visible={editRoleModalVisible}
        modalContent={
          <EditRoleModal
            role={editedRole || ({} as UserRole)}
            entities={permissionEntities}
            editRole={editRole}
          />
        }
        toggle={() => setEditRoleModalVisible(false)}
        headerText='Edit Role'
        headerClass={baseStyles.modalHeader}
      />
    </>
  );
};
